import useAbility from '@/hooks/use-ability'
import { useNavigate } from 'react-router-dom'
import Collapsible from 'react-collapsible'
import { FaCircleInfo } from 'react-icons/fa6'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { siteConfig } from '@/config/site'

export default function LayerControlGroup({
  baseMapVisibility,
  visibility,
  onBaseMapVisibilityChange,
  onVisibilityChange,
  layers,
  isDemoUser,
  isTutorialPage,
  hasQueriedParcels,
  hasFilteredParcels,
  setPopupContent,
}) {
  const { checkAbility } = useAbility()
  const navigate = useNavigate()

  const enableSAGBI = checkAbility('sagbi_modified_and_unmodified')
  const enableStatewideCrops = checkAbility('statewide_crops')
  const enableeWRIMSDiversionPoints = checkAbility('ewrims_diversion_points')
  const enableGSAWaterDistrict = checkAbility(
    'gsa_and_water_district_boundaries'
  )
  const enableFilterCriteria = checkAbility('search_by_criteria')
  const enablePropertyLines = checkAbility('property_lines')

  const enableStatewideParcels = true

  const mappingKeys = {
    sgma_sagbi: enableSAGBI,
    sgma_sagbi_unmod: enableSAGBI,
    provisional_crops_2023: enableStatewideCrops,
    sgma_ewrims: enableeWRIMSDiversionPoints,
    hydra_wd_vw: enableGSAWaterDistrict,
    hydra_gsa: enableGSAWaterDistrict,
    queried_parcels: enablePropertyLines,
    filtered_parcels: enableFilterCriteria,
    state_wide_parcels_2024: enableStatewideParcels,
  }

  const infos = {
    APN: 'Legal land parcel as defined by local county assessor as the authority having jurisdiction.',
    'Statewide Crops':
      'The California Department of Water Resources (DWR) has collected land use data throughout the state and used this information to develop water use estimates for statewide and regional planning efforts, including water use projections, water use efficiency evaluation, groundwater model development, and water transfers. These data are essential for regional analysis and decision making, which has become increasingly important as DWR and other state agencies seek to address resource management issues, regulatory compliance issues, environmental impacts, ecosystem services, urban and economic development, and other issues. Increased availability of digital satellite imagery, aerial photography and new analytical tools make remote sensing based land use surveys possible at a field scale that is comparable to that of DWR’s historical on the ground field surveys. Current technologies allow accurate, large-scale crop and land use identification to be performed at desired time increments, and make possible more frequent and comprehensive statewide land use information. Responding to this need, DWR sought expertise and support for identifying crop types and other land uses and quantifying crop acreages statewide using remotely sensed imagery and associated analytical techniques.        ',
    'SAGBI - Modified':
      'The Soil Agricultural Groundwater Banking Index (SAGBI) is a suitability index for groundwater recharge on agricultural land. The SAGBI is based on five major factors that are critical to successful agricultural groundwater banking: deep percolation, root zone residence time, topography, chemical limitations, and soil surface condition. More details can be found in the SAGBI article in California Agriculture. Modified overlay is theoretical; it shows SAGBI suitability groups when assuming that all soils with restrictive layers have been modified by deep tillage.',
    'SAGBI - Unmodified':
      'The Soil Agricultural Groundwater Banking Index (SAGBI) is a suitability index for groundwater recharge on agricultural land. The SAGBI is based on five major factors that are critical to successful agricultural groundwater banking: deep percolation, root zone residence time, topography, chemical limitations, and soil surface condition. More details can be found in the SAGBI article in California Agriculture. Unmodified overlay shows SAGBI suitability groups when not accounting for modifications by deep tillage.',
    'Water Rights (eWRIMS)':
      'The Electronic Water Rights Information Management System (eWRIMS) is a computer database developed by the State Water Resources Control Board to track information on water rights in California. eWRIMS contains information on Statements of Water Diversion and Use that have been filed by water diverters, as well as registrations, certificates, and water right permits and licenses that have been issued by the State Water Resources Control Board and its predecessors.',
    'Water Districts':
      'This dataset represents polygon boundaries of all public water agencies in California including public water systems, agricultural water districts, urban water districts, Federal and State water contractors, wholesalers, retailers, and other public or private utilities that deliver water to the end user.',
    GSA: "The 2014 Sustainable Groundwater Management Act (SGMA) required the formation of groundwater sustainability agencies (GSAs) in the State's high- and medium-priority groundwater basins and subbasins (basins) by the statutory deadline of June 30, 2017. Over 260 GSAs in over 140 basins were formed by that initial planning milestone. However, as the priorities of some basins have changed and numerous requests for basin boundary modifications have been approved, new GSAs will continue to be formed and existing GSAs may adopt changes that must be reflected in DWR's SGMA Portal.",
    'Groundwater Basins':
      'The dataset is a feature class showing the boundaries of 515 groundwater basins and subbasins as defined by the California Department of Water Resources as last modified by the Basin Boundary Emergency Regulation adopted on October 21, 2015 and subsequent modifications requested through the Basin Boundary Modification Request Process. The file is in ESRI geodatabase format and is intended for use with compatible GIS software. Groundwater basins are represented as polygon features and designated on the basis of geological and hydrological conditions - usually the occurrence of alluvial or unconsolidated deposits. When practical, large basins are also subdivided by political boundaries, as in the Central Valley. Basins are named and numbered per the convention of the Department of Water Resources.',
  }
  return (
    <div className="checkbox-wrapper p-2 relative bg-white flex flex-col justify-between shadow-md hover:shadow-xl rounded-md overflow-hidden transition-shadow duration-150 m-[10px]">
      <Collapsible trigger={'Base Maps'} open>
        {Object.keys(baseMapVisibility).map((baseMap) => (
          <div key={baseMap}>
            <label style={isDemoUser || isTutorialPage ? { color: '#c0c0c0' } : {}}>
              <input
                id={baseMap + '_layer'}
                type="checkbox"
                checked={baseMapVisibility[baseMap]}
                onChange={(evt) =>
                  onBaseMapVisibilityChange(baseMap, evt.target.checked)
                }
                disabled={isDemoUser || isTutorialPage}
              />
              {baseMap}
            </label>
          </div>
        ))}
      </Collapsible>
      <Collapsible trigger={'Data Layers'} open>
        {Object.keys(layers).map((layerId) => {
          // TODO: Need disussion on how to handle this

          const disable =
            isDemoUser || isTutorialPage ||
            (layers[layerId] === 'queried_parcels' && !hasQueriedParcels) ||
            (layers[layerId] === 'filtered_parcels' && !hasFilteredParcels)

          const hasAbility =
            (mappingKeys[layers[layerId]] !== undefined
              ? mappingKeys[layers[layerId]]
              : true) || isDemoUser || isTutorialPage

          return (
            <div
              key={layerId + '_layer'}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '2px',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: disable || !hasAbility ? '#c0c0c0' : '#000',
                }}
              >
                <input
                  id={layerId + '_layer'}
                  type="checkbox"
                  checked={visibility[layers[layerId]]}
                  onChange={(evt) =>
                    onVisibilityChange(layers[layerId], evt.target.checked)
                  }
                  disabled={disable || !hasAbility}
                  style={{ marginRight: '8px' }}
                />
                {layerId}
              </label>
              {!hasAbility ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="!p-0 ml-1 w-4">
                      <div
                        onClick={() => {
                          navigate(siteConfig.upgradeUrl)
                        }}
                      >
                        <img
                          src="/icons/lock.svg"
                          alt="lock"
                          className="w-4 h-4"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-[#000000] text-[12px] text-white">
                      Upgrade Plan
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : infos.hasOwnProperty(layerId) ? (
                <button
                  style={{
                    height: '20px',
                    width: '20px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    marginLeft: '4px',
                    cursor: 'pointer', // Makes it look clickable
                    backgroundColor: 'white', // Makes it visually distinct
                    borderRadius: '50%', // Gives it a circular button look
                    border: '1px solid #00ac9c', // Adds a border to define it
                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)', // Adds a shadow for depth
                    transition: 'box-shadow 0.3s ease, transform 0.3s ease', // Smooth hover effect
                  }}
                  onClick={() => setPopupContent(infos[layerId])}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.boxShadow =
                      '0 4px 10px rgba(0, 0, 0, 0.3)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.boxShadow =
                      '0 2px 5px rgba(0, 0, 0, 0.2)')
                  }
                >
                  <FaCircleInfo color="#00ac9c" />
                </button>
              ) : (
                <></>
              )}
            </div>
          )
        })}
      </Collapsible>
    </div>
  )
}
